/**
 * Custom modules list — /settings/custom-modules
 *
 * The entry point for the builder UI. Lists every custom module in
 * the tenant, shows source badges (custom vs marketplace), status,
 * and links to the per-module editor. A "New module" button opens
 * the create page.
 *
 * If the tenant has no custom modules yet, we show an empty state
 * with a call-to-action. The backend's admin bypass means any tenant
 * admin can create the first module without needing seeded roles.
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Box, Plus, Settings } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/')({
  component: CustomModulesListPage,
})

function CustomModulesListPage() {
  const { data: modules, isLoading } = useQuery(
    orpc.modules.custom.modules.list.queryOptions(),
  )

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Custom modules</h1>
          <p className="text-muted-foreground mt-1">
            Build your own entities, forms, and workflows. No coding required.
          </p>
        </div>
        <Link to="/settings/custom-modules/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New module
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : !modules || modules.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Box className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-medium mb-1">No custom modules yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first module to start building custom entities and workflows.
            </p>
            <Link to="/settings/custom-modules/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create module
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {modules.map((mod) => (
            <Link
              key={mod.id}
              to="/settings/custom-modules/$key"
              params={{ key: mod.key }}
            >
              <Card className="hover:bg-muted/50 transition-colors cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg">{mod.name}</CardTitle>
                    <Badge variant={mod.source === 'marketplace' ? 'default' : 'secondary'}>
                      {mod.source}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {mod.description || 'No description.'}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="font-mono">{mod.key}</span>
                    <span>v{mod.version}</span>
                    <span className="inline-flex items-center gap-1">
                      <Settings className="h-3 w-3" />
                      {mod.status}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
